import React, {FC} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useClipboard} from '../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import errorImgSrc from '../../../assets/images/error2.png';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {openHelpChatAction} from '../../../store/panels/app/appPanelActions';
import {AnalyticsInfoState} from './analyticsInfoState';

/*
 * Styles.
 */

const StyledAnalyticsErrorStateWrapperDiv = styled.div`
  background-color: ${(p) => p.theme.greys.shade20};
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

/*
 * Props.
 */

interface AnalyticsErrorStateProps {
  copyableErrorString: string;
  panelId: AnalyticsPanelId;
  className?: string;
}

/*
 * Component.
 */

export const AnalyticsErrorState: FC<AnalyticsErrorStateProps> = ({copyableErrorString, panelId}) => {
  const localize = useLocalize();
  const {copy} = useClipboard();
  const dispatch = useAppDispatch();

  const onContactUsClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.HELP_WIDGET_SUPPORT_CLICK);
    dispatch(openHelpChatAction(panelId));
  };

  return (
    <StyledAnalyticsErrorStateWrapperDiv>
      <AnalyticsInfoState
        imgPath={errorImgSrc}
        title={localize(
          defineMessage({
            id: 'analytics-metric-error-header',
            message: 'Oops, something went wrong!',
          }),
        )}
        body={
          <Trans id="analytics-metric-error-body">
            Our systems are running into an issue right now. Try reloading and if this persists, contact us
            with a copy-paste of the error details.
          </Trans>
        }
        actions={
          <>
            <Button
              variant={ButtonVariantsEnum.SECONDARY}
              leftIcon="copySmall"
              onClick={() => copy(copyableErrorString)}
            >
              <Trans id="analytics-metric-error-copy-details">Copy error details</Trans>
            </Button>
            <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onContactUsClick}>
              <Trans id="analytics-metric-error-contact-us">Contact us</Trans>
            </Button>
          </>
        }
      />
    </StyledAnalyticsErrorStateWrapperDiv>
  );
};
